import { MapPin, Phone, Mail, Clock, Instagram } from "lucide-react";
import useReveal from "../../hooks/useReveal";
import { BRAND } from "../../lib/data";

export const Contact = () => {
  const headRef = useReveal();
  const gridRef = useReveal();

  const blocks = [
    {
      icon: MapPin,
      label: "Adresse",
      primary: BRAND.address.line1,
      secondary: BRAND.address.line2,
      href: BRAND.address.mapsUrl,
      external: true,
      testId: "info-address",
    },
    {
      icon: Phone,
      label: "Téléphone",
      primary: BRAND.phone,
      href: BRAND.phoneHref,
      testId: "info-phone",
    },
    {
      icon: Mail,
      label: "E-mail",
      primary: BRAND.email,
      href: BRAND.emailHref,
      testId: "info-email",
    },
    {
      icon: Clock,
      label: "Horaires",
      primary: "Tous les jours",
      secondary: "8h — 1h",
      testId: "info-hours",
    },
    {
      icon: Instagram,
      label: "Instagram",
      primary: "@zenithplage",
      href: BRAND.instagramUrl,
      external: true,
      testId: "info-instagram",
    },
  ];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-zp-navy text-zp-cream py-24 md:py-40 relative overflow-hidden"
    >
      {/* Decorative striped accents */}
      <div className="absolute -bottom-10 -right-10 w-48 h-48 cabana-stripes-yellow opacity-90 rotate-12 hidden md:block" />
      <div className="absolute -top-12 -left-12 w-40 h-40 cabana-stripes-blue opacity-30 -rotate-12 hidden md:block" />

      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 relative">
        <div ref={headRef} className="reveal mb-16 md:mb-24 max-w-3xl">
          <p className="overline text-zp-lemon mb-6 flex items-center gap-3">
            <span className="dot" /> Nous trouver
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            On vous attend
            <br />
            <span className="italic font-light text-zp-lemon">
              les pieds dans le sable.
            </span>
          </h2>
          <p className="mt-8 text-white/75 text-base md:text-lg font-light max-w-xl leading-relaxed">
            Plage privée à Palavas-les-Flots, ouverte tous les jours de
            8h à 1h. Pour toute demande, un appel direct reste le plus
            simple — nous serons ravis de vous accueillir.
          </p>
        </div>

        {/* Info grid */}
        <div
          ref={gridRef}
          className="reveal grid md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-12 md:gap-y-16 border-t border-white/15 pt-14"
        >
          {blocks.map((b, i) => {
            const Icon = b.icon;
            const inner = (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <Icon className="text-zp-lemon shrink-0" size={18} />
                  <span className="overline text-xs text-white/60">
                    {b.label}
                  </span>
                </div>
                <div className="font-serif text-2xl md:text-3xl leading-snug group-hover:text-zp-lemon transition-colors duration-300">
                  {b.primary}
                  {b.secondary && (
                    <>
                      <br />
                      <span className="italic font-light text-white/85 group-hover:text-zp-lemon transition-colors">
                        {b.secondary}
                      </span>
                    </>
                  )}
                </div>
              </>
            );

            return b.href ? (
              <a
                key={i}
                href={b.href}
                {...(b.external
                  ? { target: "_blank", rel: "noreferrer" }
                  : {})}
                data-testid={b.testId}
                className="group block"
              >
                {inner}
              </a>
            ) : (
              <div key={i} data-testid={b.testId} className="group">
                {inner}
              </div>
            );
          })}
        </div>

        {/* CTA bar */}
        <div className="mt-16 md:mt-24 pt-10 border-t border-white/15 flex flex-col md:flex-row gap-6 md:items-center md:justify-between">
          <p className="font-serif italic text-2xl md:text-3xl text-white/90 max-w-2xl">
            Une question, une grande tablée, un événement ?
          </p>
          <a
            href={BRAND.phoneHref}
            data-testid="contact-call-cta"
            className="btn-accent self-start md:self-auto"
          >
            <Phone size={16} />
            Appelez-nous
          </a>
        </div>
      </div>
    </section>
  );
};

export default Contact;
